/**
 * SignInContainer
 * --
 */
import { useRef, useState } from 'react';
import SignInPresenter from './SignInPresenter';
import {
  KAKAO_CLIENT_KEY,
  SOCIAL_CALLBACK_URL,
  NAVER_CLIENT_KEY,
  GOOGLE_CLIENT_KEY,
  FACEBOOK_CLIENT_KEY,
  APPLE_CLIENT_KEY,
  removeCookie,
  deleteCookie,
  HOST_URL,
} from 'utils';
import { useAuthCheck, useUserLogin } from 'hooks/AuthHooks';
import MessageAlert from 'utils';
import { useAccountAction, useAccountStack, useUserAction } from 'store';
import { LoadingPage } from 'components';

const SignInContainer = (props) => {
  /* ===== Props ===== */
  const { navigate, location, screenSize } = props;

  const from = location.state?.from || '/main';
  // console.log('from', from);

  /* ===== Mutate =====*/
  // 로그인
  const { loginUser } = useUserLogin();
  // 토큰 검증
  const { authCheck } = useAuthCheck();

  /* ===== Store =====*/
  // User
  const userAction = useUserAction();
  // Account
  const accountAction = useAccountAction();
  const userAccount = useAccountStack();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const accountRef = useRef(null);
  const passwordRef = useRef(null);
  const [status, setStatus] = useState('default');
  const [message, setMessage] = useState('message');
  // 로그인 정보
  const [loginInfo, setLoginInfo] = useState({
    user_account: userAccount ?? '',
    password: '',
    is_save: userAccount ? true : false,
  });
  // 로딩
  const [isLoading, setIsLoading] = useState(false);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 로그인 함수
   * --
   */
  const handleLogin = async () => {
    const { user_account, password, is_save } = loginInfo;

    if (!user_account) {
      MessageAlert.warning('아이디를 입력해주세요!');
      setMessage('아이디를 입력해주세요!');
      setStatus('error');
      accountRef.current.focus();
      return;
    }

    if (!password) {
      MessageAlert.warning('비밀번호를 입력해주세요!');
      setMessage('비밀번호를 입력해주세요!');
      setStatus('error');
      passwordRef.current.focus();
      return;
    }

    try {
      setIsLoading(true);
      const loginData = {
        user_account,
        password,
      };

      const { data, status } = await loginUser(loginData);

      // NOTE: 로그인 실패  처리
      if (status !== 200) {
        MessageAlert.warning(
          '아이디 또는 비밀번호를 잘못 입력했습니다. 다시 확인해주세요.'
        );
        setMessage(
          '아이디 또는 비밀번호를 잘못 입력했습니다. 다시 확인해주세요.'
        );
        setStatus('error');
        accountRef.current.focus();
        setIsLoading(false);
        return;
      }

      // NOTE: 아이디 저장
      if (is_save) {
        accountAction.setAccount(user_account);
      } else {
        accountAction.removeAccount();
      }

      // NOTE: 쿠키 저장 시간 설정
      const saveTime = 8 * 60 * 60 * 1000; // 8h
      removeCookie('JM_COFFEE_ACCESS_TOKEN', data?.token, saveTime);

      // NOTE: 토큰 검증
      handleTokenCheck();
    } catch (err) {
      MessageAlert.error('로그인 중 오류가 발생하였습니다!');
      setIsLoading(false);
      console.error('[LOGIN] ERROR!! ', err);
      throw err;
    }
  };

  /**
   * 토큰 검증
   * --
   */
  const handleTokenCheck = async () => {
    // 토큰 검증
    try {
      const { status, data } = await authCheck();

      // NOTE: 검증 실패 처리
      if (status !== 200) {
        userAction.resetUser();
        deleteCookie('JM_COFFEE_ACCESS_TOKEN', {});
        return;
      }

      // NOTE: 검증 성공
      userAction.setUser(data);
      navigate(from, { replace: true });
    } catch (err) {
      MessageAlert.error('로그인 중 오류가 발생하였습니다!');
      setIsLoading(false);
      console.error('[AUTH] ERROR!! ', err);
      throw err;
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = null) => {
    let newData = { ...loginInfo };

    newData[t] = v;
    const d = {
      ...newData,
    };
    setLoginInfo(d);

    if (callback) callback(v);
  };

  /**
   * 소셜로그인 함수
   * @param type : string
   * --
   */
  const handleSocialLogin = (type) => {
    let socialParams;

    switch (type) {
      case 'kakao':
        socialParams = {
          clientId: `${KAKAO_CLIENT_KEY}`,
          redirectUri: `${SOCIAL_CALLBACK_URL}?type=kakao`,
          codeUrl: 'https://kauth.kakao.com/oauth/authorize',
          scope: ['profile_nickname', 'profile_image', 'account_email', 'name'],
        };
        break;
      case 'naver':
        socialParams = {
          clientId: `${NAVER_CLIENT_KEY}`,
          redirectUri: `${SOCIAL_CALLBACK_URL}?type=naver`,
          codeUrl: 'https://nid.naver.com/oauth2.0/authorize',
          scope: 'profile',
        };
        break;
      case 'google':
        socialParams = {
          clientId: GOOGLE_CLIENT_KEY,
          redirectUri: `${SOCIAL_CALLBACK_URL}?type=google`,
          codeUrl: 'https://accounts.google.com/o/oauth2/auth',
          scope: 'email profile',
        };
        break;
      case 'facebook':
        socialParams = {
          clientId: FACEBOOK_CLIENT_KEY,
          redirectUri: `${SOCIAL_CALLBACK_URL}?type=facebook`,
          codeUrl: 'https://www.facebook.com/v10.0/dialog/oauth',
          scope: 'email,public_profile',
        };
        break;
      case 'apple':
        socialParams = {
          clientId: APPLE_CLIENT_KEY,
          // redirectUri: `${SOCIAL_CALLBACK_URL}?type=apple`,
          redirectUri: `${HOST_URL}/auth/social/callback?type=apple`,
          codeUrl: 'https://appleid.apple.com/auth/authorize',
          scope: 'email name',
          responseMode: 'form_post',
        };
        break;
      default:
        console.error('Unknown social type:', type);
        return;
    }

    const url = new URL(socialParams.codeUrl);
    const params = new URLSearchParams({
      client_id: socialParams.clientId,
      redirect_uri: socialParams.redirectUri,
      response_type: 'code',
      scope: socialParams.scope || '', // scope는 선택적
      ...(socialParams.responseMode && {
        response_mode: socialParams.responseMode,
      }),
    });
    url.search = params.toString();

    window.location.href = url.toString();
  };

  /* ===== HOOKS ===== */

  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'로그인중입니다. 잠시만 기다려주세요'} />
      )}

      <SignInPresenter
        accountRef={accountRef}
        passwordRef={passwordRef}
        inputValue={loginInfo}
        status={status}
        message={message}
        onSubmit={handleLogin}
        onChange={handleChange}
        navigate={navigate}
        onSocialLogin={handleSocialLogin}
        screenSize={screenSize}
      />
    </>
  );
};

export default SignInContainer;
